import type { BuyUniverseFieldKeys } from './fields'
import type { FilterObject } from './filter-expression'
import type { ReferenceDataField } from './instrument-data'

export interface FilterResponse {
  id: string
  name: string
  version: string
  type: FilterType
  filter: FilterObject
}

export interface CreateFilterRequest {
  name: string
  version: string
  type: FilterType
  // Should contain "type" field if you want to filter by asset class
  filter: FilterObject
}

export interface UpdateFilterRequest {
  id: string
  name: string
  version: string
  type: FilterType
  // Should contain "type" field if you want to filter by asset class
  filter: FilterObject
}

export enum FilterType {
  BondScreener = 'bond-screener',
  HoldingList = 'holding-list',
  AccountList = 'account-list',
}

export enum Operator {
  EqualTo = 'equal_to',
  NotEqualTo = 'not_equal_to',
  GreaterThan = 'greater_than',
  GreaterThanOrEqualTo = 'greater_than_or_equal_to',
  LessThan = 'less_than',
  LessThanOrEqualTo = 'less_than_or_equal_to',
  In = 'in',
  NotIn = 'not_in',
  IsNull = 'is_null',
  Includes = 'includes',
  NotIncludes = 'not_includes',
  StartsWith = 'starts_with',
  NotStartsWith = 'not_starts_with',
  EndsWith = 'ends_with',
  InFilterList = 'in_filter_list',
  NotInFilterList = 'not_in_filter_list',
  NotInList = 'not_in_list',
}

export enum FilterValueType {
  String = 'string',
  StringFlex = 'string_flex',
  Number = 'number',
  Boolean = 'boolean',
  Date = 'date',
  Rating = 'rating',
  Percentage = 'percentage',
}

export type FilterValue = string | string[] | number | number[] | boolean

/**
 * FilterBuilderResponse is the criteria to filter the field
 * - field: the field to filter on
 * - operator: the operator to filter with
 * - inputValue: the value to filter on. If the operator is in or not_in, we want to pass in an array of the values
 */
export interface FilterBuilderResponse {
  field: ReferenceDataField | BuyUniverseFieldKeys
  operator: Operator | null
  inputValue: FilterValue
}

/**
 * Need this to better interact with <FilterBuilder />
 * as it returns null for optional fields.
 */
interface NullableFilterBuilderResponse {
  field: ReferenceDataField | null
  operator: Operator | null
  inputValue: string | null
}

export type FilterBuilderResponseUnion =
  | FilterBuilderResponse
  | NullableFilterBuilderResponse

// Branded so only the custom-column boundary mints one.
export type CustomFieldName = string & { readonly __brand: 'CustomFieldName' }

// FilterableField is the field to filter on
export interface FilterableField {
  // 'price' is the optimizer's live/eval pricing key used in model universe
  // filters; it has no reference-data column of its own.
  id:
    | ReferenceDataField
    | BuyUniverseFieldKeys
    | CustomFieldName
    | 'days_to_maturity'
    | 'price'
  label: string
  type: FilterValueType
  dropdownOptions?: string[]
}
